<template>
    <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
            <div class="card-icon">
                <md-icon>inventory</md-icon>
            </div>
        </md-card-header>
        <md-card-content>
            <p v-if="!items || items.length === 0" class="grayedout-text text-center">
                No items found
            </p>
            <div v-else>
                <div class="item-table-container" :class="{ 'with-scroll': enableScroll }">
                    <md-table class="context-menu-support custom-paginated-table">
                        <md-table-row>
                            <md-table-head>Reference</md-table-head>
                            <md-table-head>Status</md-table-head>
                            <md-table-head>Barcode</md-table-head>
                            <md-table-head>Description</md-table-head>
                            <md-table-head
                                v-if="$_isLoadCapacityTypeEnabled(loadCapacityList, 'Volume')"
                                class="volume-column"
                            >
                                V
                            </md-table-head>
                            <md-table-head
                                v-if="$_isLoadCapacityTypeEnabled(loadCapacityList, 'Weight')"
                                class="width-column"
                            >
                                Wt
                            </md-table-head>
                            <md-table-head v-if="$root.isDesktop && !isReadOnlyUser" class="actions-column">
                                Actions
                            </md-table-head>
                        </md-table-row>
                        <md-table-row v-for="item in items" :key="item.itemId">
                            <md-table-cell>
                                <router-link
                                    v-if="!item.shipmentDetails.shipmentId"
                                    :to="{ name: 'Stop Details', params: { stopId: item.stopId } }"
                                    target="_blank"
                                >
                                    <template v-if="item.stopDetails.sourceReference">
                                        {{ item.stopDetails.sourceReference }}
                                    </template>
                                    <template v-else>
                                        {{ item.stopDetails.stopRef }}
                                    </template>
                                </router-link>
                                <router-link
                                    v-else
                                    :to="{
                                        name: 'Shipment Details',
                                        params: { shipmentId: item.shipmentDetails.shipmentId }
                                    }"
                                    target="_blank"
                                >
                                    <template v-if="item.shipmentDetails.sourceReference">
                                        {{ item.shipmentDetails.sourceReference }}
                                    </template>
                                    <template v-else>
                                        {{ item.shipmentDetails.shipmentRef }}
                                    </template>
                                </router-link>
                            </md-table-cell>
                            <md-table-cell>
                                <div
                                    :class="
                                        `custom-badge inventory-status inventory-status-${item.status
                                            .toLowerCase()
                                            .replace(/\s+/g, '')}`
                                    "
                                >
                                    {{ $t('inputs.itemStatuses.' + item.status) }}
                                </div>
                            </md-table-cell>
                            <md-table-cell>{{ item.barcode }}</md-table-cell>
                            <md-table-cell>{{ item.description }}</md-table-cell>
                            <md-table-cell
                                v-if="$_isLoadCapacityTypeEnabled(loadCapacityList, 'Volume')"
                                class="volume-column"
                            >
                                {{ $_displayLoadValue(loadCapacityList, item.load, 'Volume') }}
                            </md-table-cell>
                            <md-table-cell
                                v-if="$_isLoadCapacityTypeEnabled(loadCapacityList, 'Weight')"
                                class="width-column"
                            >
                                {{ $_displayLoadValue(loadCapacityList, item.load, 'Weight') }}
                            </md-table-cell>
                            <md-table-cell v-if="$root.isDesktop && !isReadOnlyUser" class="actions-column">
                                <update-item-button
                                    v-if="!isReadOnlyUser"
                                    :item="item"
                                    @itemUpdated="handleUpdateItem"
                                    :is-delivered="item.status === 'Delivered'"
                                    :stop-status="item.stopStatus"
                                    :is-invoiced="item.stopDetails.isInvoiced"
                                />
                            </md-table-cell>
                        </md-table-row>
                    </md-table>
                </div>
                <div class="item-totals-container" :class="{ 'with-scroll': enableScroll }">
                    <md-table class="context-menu-support custom-paginated-table">
                        <md-table-row>
                            <md-table-cell>
                                <strong>Total</strong>
                            </md-table-cell>
                            <md-table-cell></md-table-cell>
                            <md-table-cell></md-table-cell>
                            <md-table-cell class="item-count-column">
                                <strong>{{ items.length }} items</strong>
                            </md-table-cell>
                            <md-table-cell
                                v-if="$_isLoadCapacityTypeEnabled(loadCapacityList, 'Volume')"
                                class="volume-column"
                            >
                                <strong>
                                    {{ loadTotals.volume }}
                                    {{ loadCapacityList.find((l) => l.label === 'Volume').symbol }}
                                </strong>
                            </md-table-cell>
                            <md-table-cell
                                v-if="$_isLoadCapacityTypeEnabled(loadCapacityList, 'Weight')"
                                class="width-column"
                            >
                                <strong>
                                    {{ loadTotals.weight }}
                                    {{ loadCapacityList.find((l) => l.label === 'Weight').symbol }}
                                </strong>
                            </md-table-cell>
                            <md-table-cell class="actions-column"></md-table-cell>
                        </md-table-row>
                    </md-table>
                </div>
            </div>
        </md-card-content>
    </md-card>
</template>

<script>
import { mapGetters } from 'vuex';
import { GeneralMixin } from '@/mixins';
import { UpdateItemButton } from '@/pages/Inventory/buttons';

export default {
    name: 'TripItems',
    mixins: [GeneralMixin],
    components: {
        UpdateItemButton
    },
    props: {
        items: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            loadCapacityList: []
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user',
            isReadOnlyUser: 'user/isReadOnlyUser',
            isSupportStaff: 'user/isSupportStaff'
        }),
        loadTotals() {
            return this.$_getLoadTotals(this.items, ['volume', 'weight']);
        },
        enableScroll() {
            return this.items.length > 5;
        }
    },
    methods: {
        handleUpdateItem() {
            this.$emit('itemUpdated');
        }
    },
    mounted() {
        this.loadCapacityList = [];
        if (this.user.vehicleCapacityUnitsConfiguration && this.user.vehicleCapacityUnitsConfiguration.length > 0) {
            this.loadCapacityList = this.user.vehicleCapacityUnitsConfiguration.filter((x) => x.type !== 'quantity');
        }
    }
};
</script>

<style lang="scss" scoped>
.item-table-container {
    max-height: 600px;
}

.item-table-container.with-scroll {
    overflow-y: scroll;
}

.item-totals-container.with-scroll {
    padding-right: 15px;
}

.custom-badge.inventory-status {
    max-width: 100%;
}

.item-count-column {
    text-align: right;
    padding-right: 50px;
}

.volume-column {
    width: 60px;
}

.width-column {
    width: 60px;
}

.actions-column {
    width: 100px;
}
</style>
